import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Mail, Phone } from "lucide-react";
import { Input } from "../components/ui/input";
import { fetchLeads, fetchLeadStats, updateLeadStatus } from "../lib/api";

const STATUSES = ["new", "contacted", "won", "closed"];
const statusColor = {
  new: "bg-mm-green text-black",
  contacted: "bg-yellow-500 text-black",
  won: "bg-blue-500 text-white",
  closed: "bg-gray-600 text-white",
};

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("mm_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Leads Dashboard | Mobile Maintenance"; }, []);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(t), fetchLeadStats(t)]);
      setLeads(l); setStats(s); setAuthed(true);
      localStorage.removeItem("mm_admin_token");
      sessionStorage.setItem("mm_admin_token", t);
    } catch (e) {
      toast.error("Invalid admin token");
      setAuthed(false);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const changeStatus = async (id, status) => {
    try {
      await updateLeadStatus(token, id, status);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const logout = () => { sessionStorage.removeItem("mm_admin_token"); localStorage.removeItem("mm_admin_token"); setToken(""); setAuthed(false); setInput(""); };

  if (!authed) {
    return (
      <div className="min-h-screen bg-mm-base flex items-center justify-center px-5">
        <div className="beam p-8 w-full max-w-md">
          <div className="relative z-10">
            <img src="/assets/logo.png" alt="Mobile Maintenance" className="h-9 mb-6" />
            <h1 className="font-heading font-black uppercase text-3xl text-white">Leads Dashboard</h1>
            <p className="mt-2 text-gray-400 text-sm">Enter your admin token to continue.</p>
            <form onSubmit={(e) => { e.preventDefault(); setToken(input); }} className="mt-6 space-y-4">
              <Input
                data-testid="admin-token-input"
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Admin token"
                className="rounded-none bg-mm-panel border-mm-green/20 text-white h-12 focus-visible:ring-mm-green"
              />
              <button data-testid="admin-login-btn" className="w-full font-heading font-bold uppercase tracking-wider bg-mm-green text-black py-3 hover:bg-white transition-colors flex items-center justify-center gap-2">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Access Dashboard"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mm-base">
      <header className="border-b border-mm-green/20 bg-mm-surface">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-20 flex items-center justify-between">
          <img src="/assets/logo.png" alt="Mobile Maintenance" className="h-9" />
          <div className="flex items-center gap-3">
            <button onClick={() => load(token)} className="p-2.5 border border-mm-green/25 text-mm-green hover:bg-mm-green hover:text-black transition-colors" data-testid="admin-refresh"><RefreshCw size={16} /></button>
            <button onClick={logout} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-mm-green/25 text-gray-300 px-4 py-2.5 hover:text-mm-green transition-colors" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 sm:px-8 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-mm-green/15 border border-mm-green/15 mb-10">
          {[{ l: "Total Leads", v: stats.total }, { l: "New / Unactioned", v: stats.new }, { l: "Showing", v: leads.length }].map((c) => (
            <div key={c.l} className="bg-mm-surface p-6">
              <div className="font-heading font-black text-5xl text-mm-green">{c.v}</div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-gray-400">{c.l}</div>
            </div>
          ))}
        </div>

        {leads.length === 0 ? (
          <p className="text-gray-500 font-mono text-sm py-20 text-center">No leads yet.</p>
        ) : (
          <div className="space-y-px bg-mm-green/15 border border-mm-green/15">
            {leads.map((l) => (
              <div key={l.id} className="bg-mm-surface p-6 flex flex-col lg:flex-row lg:items-center gap-5" data-testid={`lead-row-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-heading font-bold text-xl text-white uppercase">{l.name}</span>
                    {l.company && <span className="text-gray-500 text-sm">/ {l.company}</span>}
                    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${statusColor[l.status] || "bg-gray-600"}`}>{l.status}</span>
                    {!l.email_sent && <span className="px-2 py-0.5 text-[10px] font-mono uppercase bg-red-900/60 text-red-300">email off</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-400">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-mm-green"><Mail size={13} />{l.email}</a>
                    <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 hover:text-mm-green"><Phone size={13} />{l.phone}</a>
                    <span className="text-mm-green">{l.service}</span>
                    {l.location && <span>{l.location}</span>}
                    {l.fleet_size && <span>Fleet: {l.fleet_size}</span>}
                  </div>
                  {l.message && <p className="mt-2 text-sm text-gray-500 italic">{`"${l.message}"`}</p>}
                  <div className="mt-2 font-mono text-[11px] text-gray-600">{new Date(l.created_at).toLocaleString()}</div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => changeStatus(l.id, s)}
                      className={`font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${l.status === s ? "border-mm-green text-mm-green" : "border-mm-green/15 text-gray-500 hover:text-white"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
